export const DEFAULT_SLINK = import.meta.env.VITE_SLINK_SDUI_GMBH_SCHOOL
export const DEFAULT_BRANDING = import.meta.env.VITE_DEFAULT_BRANDING || 'DEFAULT'
// since DEFAULT_BRANDING can be overwritten via .env file, we need to make sure that language-fallback-logic
// still uses 'DEFAULT'
export const DEFAULT_TRANSLATION_BRANDING = 'DEFAULT'
export const DEFAULT_IDENTITY_PROVIDER = 'SDUI'

/* CLASSBOOK */
export const ENTITY_TYPE = {
  allGrades: 'allGrades',
  allUsers: 'allUsers',
  allCourses: 'allCourses',
}

/**
 * Classbook Entities
 * @param instance : Vue
 * @param count
 * @returns {{courses: {id: string}, grades: {meta: {users_count: string, color: string, displayname: VueI18n.TranslateResult}, id: string}, users: {id: string}}}
 */
export const entities = (instance: any, count: number, afterDisplayname: boolean = false) => ({
  users: {
    id: ENTITY_TYPE.allUsers,
    iconClass: 'icon-group type-student type-student-light-bg sm icon circle',
    meta: {
      displayname: `${instance.$t('classbook.entities.all')} ${afterDisplayname || instance.$t('classbook.entities.users')}`,
      subtitle: `${count} ${instance.$t('classbook.entities.users')}`,
    },
  },
  courses: {
    id: ENTITY_TYPE.allCourses,
    meta: {
      color: 'primary-dark-bg white',
      shortname: `${instance.$t('classbook.entities.all')}`,
      displayname: `${instance.$t('classbook.entities.all')} ${afterDisplayname || instance.$t('classbook.entities.courses')}`,
      name: `${count} ${instance.$t('classbook.entities.courses')}`,
    },
  },
  grades: {
    id: ENTITY_TYPE.allGrades,
    meta: {
      displayname: `${instance.$t('classbook.entities.all')} ${afterDisplayname || instance.$t('classbook.entities.grades')}`,
      users_count: `${count} ${instance.$t('classbook.entities.grades')}`,
      color: 'primary',
    },
  },
})

/**
 * Classbook Constants
 * @type {{classbook: {absences: {types: {STANDARD_TYPE_ID: string, keys: {UNEXCUSED: string, missing: string, PENDING: string, none: string, EXCUSED: string}, STANDARD_TYPE_CATEGORY: string, colors: {UNEXCUSED: string[], missing: string[], PENDING: string[], none: string[], EXCUSED: string[]}}, status: {deleted: string, read: string, updated: string}}}}}
 */
const constants = {
  classbook: {
    absences: {
      types: {
        STANDARD_TYPE_ID: 'missing',
        STANDARD_TYPE_CATEGORY: 'sick',
        keys: {
          UNEXCUSED: 'UNEXCUSED',
          EXCUSED: 'EXCUSED',
          PENDING: 'PENDING',
          MISSING: 'missing',
          NONE: 'none',
        },
        groupBy: {
          MISSING_OPEN: 'MISSING-OPEN',
          MISSING_CONTACTED: 'MISSING-CONTACTED',
          UNREAD: 'UNREAD',
          CURRENT: 'CURRENT',
          PAST: 'PAST',
        },
        colors: {
          UNEXCUSED: 'danger',
          EXCUSED: 'success',
          PENDING: 'success-dark',
          missing: 'danger-dark',
          none: 'gray-300',
        },
      },
      status: {
        deleted: 'DELETED',
        read: 'READ',
        updated: 'UPDATED',
        contacted: 'CONTACTED',
        missing_parents: 'MISSING_PARENTS',
      },
      filter: {
        CURRENT: 'CURRENT',
        ALL: 'ALL',
      },
      order: {
        DEFAULT: 'default',
        PRIORITY: 'priority',
      },
    },
  },
  notificationCentre: {
    sourceTypes: {
      CALENDAR_EVENT: 'EVENT',
    },
    staticListItemActions: {
      TYPE_KEY: 'LIST_ITEM_ACTION',
      OPEN_DETAILS: 'OPEN_DETAILS',
      LINK_INTERNAL: 'LINK_INTERNAL',
      LINK_EXTERNAL: 'LINK_EXTERNAL',
    },
  },
  survey: {
    question_type: {
      SINGLE_CHOICE: 'single',
      MULTIPLE_CHOICE: 'multiple',
      FREE_TEXT: 'freetext',
    },
    resultsVisibility: {
      NONE: 'none',
      BEFORE: 'before',
      AFTER: 'after',
    },
    maxQuestions: 20,
    maxCharacters: 511,
    maxVisibleFreetextAnswers: 4,
  },
  message: {
    types: {
      MESSAGE: 'MESSAGE',
      VOICE: 'VOICE',
      KNOCK: 'KNOCK',
      NC_EVENT: 'NC_EVENT',
      HINT: 'HINT',
    },
    targetTypes: {
      SURVEYS: 'surveyForm',
      CONFERENCES: 'conferences',
      EVENTS: 'event',
      NEWS: 'news',
    },
  },
}

export const SCHOOL_PROPERTIES = {
  SCHOOL_PROPERTY_STUDENTS_CONTACT: 'users.students.contact.teachers',
  SCHOOL_PROPERTY_PARENTS_CONTACT: 'users.parents.contact.teachers',
  SCHOOL_PROPERTY_NAMES_LOCKED: 'users.names_locked',
  SCHOOL_PROPERTY_CLASSBOOK: 'classbooks.enabled',
  SCHOOL_PROPERTY_CLASSBOOK_PDF: 'classbooks.pdf.enabled',
  SCHOOL_PROPERTY_CLASSBOOK_ABSENCE_RIGHTS: 'classbooks.absence_rights.enabled',
  SCHOOL_PROPERTY_MESSAGES_DELETABLE: 'channels.messages.deletable',
  SCHOOL_PROPERTY_CHAT_SEARCH: 'channels.chat_search.enabled',
  SCHOOL_PROPERTY_CONFERENCES: 'channels.conferences',
  SCHOOL_PROPERTY_SDUIPLUS: 'sduiplus.enabled',
  SCHOOL_PROPERTY_KNOCKING: 'knocking.enabled',
  SCHOOL_PROPERTY_PERIODS: 'timetables.periods',
  SCHOOL_PROPERTY_COURSENAMEGENERATION: 'timetables.course_name_generation.enabled',
  SCHOOL_PROPERTY_ACTIVATIONCODE: 'users.students.activation_code.hidden',
  SCHOOL_PROPERTY_SICKNOTES: 'sick-notes.enabled',
  SCHOOL_PROPERTY_SICKNOTES_PARENT_NOTIFICATION: 'sick_notes.parent_absence_notifications.enabled',
  SCHOOL_PROPERTY_TRANSLATIONS: 'news.translations_disabled',
  SCHOOL_PROPERTY_GRADES: 'users.grades_locked',
  SCHOOL_PROPERTY_VOICEMEMOS: 'voice.enabled',
  SCHOOL_PROPERTY_SMS: 'news.emergency_sms',
  SCHOOL_PROPERTY_HARD_DELETION: 'users.hard_delete',
  SCHOOL_PROPERTY_CALENDAR: 'calendars.enabled',
  SCHOOL_PROPERTY_CONTENTMOVE: 'channels.content_move.enabled',
  SCHOOL_PROPERTY_TIMETABLEVIEW: 'timetables.view.enabled',
  SCHOOL_PROPERTY_FAQ: 'faq.enabled',
  SCHOOL_PROPERTY_SURVEY: 'channels.surveys',
  SCHOOL_PROPERTY_TRACKING: 'users.is_trackable.enabled',
  SCHOOL_PROPERTY_AI_CHAT: 'ai.chat.enabled',
  SCHOOL_PROPERTY_AI_WRITING_TOOL: 'ai.writing_tool.enabled',
  SCHOOL_PROPERTY_PUPIL_PARENT_MEETINGS: 'pupil.parent_meetings.enabled',
  SCHOOL_PROPERTY_PUPIL_MEDIA_LIBRARY: 'pupil.media_library.enabled',
  SCHOOL_PROPERTY_PUPIL_MEETING_PROTOCOLS: 'pupil.meeting_protocols.enabled',
  SCHOOL_PROPERTY_PUPIL_INVENTORY: 'pupil.inventory.enabled',
  SCHOOL_PROPERTY_PUPIL_SOCIAL_MANAGEMENT: 'pupil.social_management.enabled',
  SCHOOL_PROPERTY_AI_ASSISTANT: 'ai.ai_assistant.enabled',
  SCHOOL_PROPERTY_AI_GRADES_AND_CERTIFICATIONS: 'ai.grades_and_certifications.enabled',
  SCHOOL_PROPERTY_EDUCA_SEVEN_TIMETABLE: 'educa.timetable.enabled',
  SCHOOL_PROPERTY_IAM_AS_AUTH_DRIVER: 'iam.as_auth_driver.enabled',
  SCHOOL_PROPERTY_STEP_UP_AUTH: 'iam.step_up_auth.enabled',
  SCHOOL_PROPERTY_TIMETABLE_VIEW_RESTRICTED: 'timetables.teacher_view_restricted.enabled',
  SCHOOL_PROPERTY_SCHOOLYEAR_CHANGE: 'users.schoolyear_change.enabled',
  SCHOOL_PROPERTY_MUTED_DAYS_ENABLED: 'channels.school_muted_days.enabled',
  SCHOOL_PROPERTY_MUTED_DAYS: 'channels.school_muted_days',
  SCHOOL_PROPERTY_ODS_DATA_SYNC_ENABLED: 'ods_data_sync.enabled',
  SCHOOL_PROPERTY_ABSENCE_TYPES_EDITABLE: 'classbooks.absence_types.editable',
}

// Property keys registered on the BE for storage/validation but not rendered as
// toggle rows (they represent data values such as JSON config, not toggleable
// feature flags). Their value is still available via the school's properties
// map for whatever consumer needs it.
export const NON_TOGGLE_PROPERTY_KEYS: string[] = [
  SCHOOL_PROPERTIES.SCHOOL_PROPERTY_MUTED_DAYS,
]

export const RELATIONSHIPS = {
  father: 'FATHER',
  mother: 'MOTHER',
  other: 'OTHER',
}
export const DEFAULT_RELATIONSHIP_OPTIONS = [RELATIONSHIPS.father, RELATIONSHIPS.mother, RELATIONSHIPS.other]

export const DISCOVERY_FEATURES = {
  AI_SCHEDULE_PLANNER: 'ai_schedule_planner',
  DAY_CARE: 'day_care',
  ROOM_BOOKING: 'room_booking',
  DIGITAL_GRADE_BOOK: 'digital_grade_book',
  LESSON_PLANNING: 'lesson_planning',
  LEARNING_REPORTS: 'learning_reports',
  PARENT_MEETINGS: 'parent_meetings',
  MEDIA_LIBRARY: 'media_library',
  MEETING_PROTOCOLS: 'meeting_protocols',
  INVENTORY: 'inventory',
  SOCIAL_MANAGEMENT: 'social_management',
  AI_ASSISTANT: 'ai_assistant',
  GRADES_AND_CERTIFICATIONS: 'grades_and_certifications',
  SEVEN_TIMETABLE: 'seven_timetable',
}

const APP = {
  SDUI: 'Sdui',
  ADDITIO: 'Additio',
  PUPIL: 'Pupil'
}

export const DISCOVERY_FEATURE_CARDS = [
  {
    key: DISCOVERY_FEATURES.AI_SCHEDULE_PLANNER,
    link: 'more.group_features.ai_schedule_planner.landing_page_url',
    color: 'primary',
    svgIcon: 'AIPlannerIcon',
    title: 'more.group_features.ai_schedule_planner.title',
    productBadge: 'more.group_features.badges.communication',
    appBadge: APP.SDUI
  },
  {
    key: DISCOVERY_FEATURES.ROOM_BOOKING,
    link: 'more.group_features.room_booking.landing_page_url',
    color: 'indigo',
    icon: 'icon-monitor',
    title: 'more.links.cards.room_booking.title',
    productBadge: 'more.group_features.badges.administration',
    appBadge: APP.PUPIL
  },
  {
    key: DISCOVERY_FEATURES.DAY_CARE,
    link: 'more.group_features.day_care.landing_page_url',
    color: 'indigo',
    svgIcon: 'DaycareIcon',
    title: 'more.links.cards.daycare.title',
    productBadge: 'more.group_features.badges.administration',
    appBadge: APP.PUPIL
  },
  {
    key: DISCOVERY_FEATURES.DIGITAL_GRADE_BOOK,
    link: 'more.group_features.grade_book.landing_page_url',
    color: 'orange',
    svgIcon: 'GradeIcon',
    title: 'more.group_features.grade_book.title',
    productBadge: 'more.group_features.badges.learning_management',
    appBadge: APP.ADDITIO
  },
  {
    key: DISCOVERY_FEATURES.LESSON_PLANNING,
    link: 'more.group_features.lesson_planning.landing_page_url',
    color: 'orange',
    svgIcon: 'LessonIcon',
    title: 'more.group_features.lesson_planning.title',
    productBadge: 'more.group_features.badges.learning_management',
    appBadge: APP.ADDITIO
  },
  {
    key: DISCOVERY_FEATURES.LEARNING_REPORTS,
    link: 'more.group_features.learning_development.landing_page_url',
    color: 'orange',
    icon: 'icon-note',
    title: 'more.group_features.learning_development.title',
    productBadge: 'more.group_features.badges.learning_management',
    appBadge: APP.ADDITIO
  }
]

export const TIME_GRID = {
  DEFAULT_SWIPER_OPTIONS: {
    slidesPerView: 1,
    speed: 100,
    autoHeight: true,
    spaceBetween: 15,
    threshold: 8,
    keyboard: {
      enabled: false, // disabled by default because we implement our own keyboard-swipe listeners
    },
  },
  DEFAULT_SELECTED_DAY: -1,
  VIEW_TYPES: {
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
  },
  MAX_DAYS_TO_SHOW: 7,
  TIMETABLE: {
    DEFAULT_DAYS_TO_SHOW: 5,
    SHORT_BREAK_MAX_DURATION_MINUTES: 7,
    DEFAULT_COLUMN_HEIGHT_SCALE: 1.45,
  },
  CALENDAR: {
    DEFAULT_DAYS_TO_SHOW: 5,
    DEFAULT_COLUMN_HEIGHT_SCALE: 1.367,
  },
}

export const AUTH_TYPE = {
  KEYCLOAK: 'keycloak',
  PASSPORT: 'passport'
} as const

export const BROADCASTING_PROVIDER = {
  ECHO: 'echo',
  CENTRIFUGO: 'centrifugo',
}

// Whenever a request is made to one of these urls, skip waiting for the refresh token
export const REFRESH_TOKEN_IGNORED_URLS = ['/devices/revoke']

export default constants
